import * as React from 'react';

import { Link } from 'react-router-dom';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

/**
 * Composable primitives for the "Linear-style" settings content layout:
 * a plain-text heading above a bordered card whose rows pair a
 * `title + description` on the left with a control on the right.
 */

const SettingsContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-settings-section="content"
      className={cn('mx-auto mb-32 w-full max-w-3xl space-y-12 py-4', className)}
      {...props}
    />
  ),
);
SettingsContent.displayName = 'SettingsContent';

/**
 * A "Linear-style" back link for drilling out of a settings sub-page back to
 * its parent section. Rendered above the sub-page content.
 */
const SettingsBackLink = React.forwardRef<HTMLAnchorElement, { to: string; label: string; className?: string }>(
  ({ to, label, className }, ref) => (
    <Button
      asChild
      variant="ghost"
      size="sm"
      className={cn('mb-2 -ml-1.5 w-fit gap-1 px-1.5 text-sm text-gray-500 hover:text-gray-900', className)}
    >
      <Link ref={ref} to={to} data-settings-section="back-link">
        <Icon className="lucide--chevron-left size-4" />
        {label}
      </Link>
    </Button>
  ),
);
SettingsBackLink.displayName = 'SettingsBackLink';

const SettingsSectionHeading = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    title: string;
    description?: React.ReactNode;
    action?: React.ReactNode;
    /** Horizontal inset that aligns the heading with the bordered card's rows. Disable when there's no card below. */
    inset?: boolean;
  }
>(({ title, description, action, inset = true, className, ...props }, ref) => (
  <div
    ref={ref}
    data-settings-section="heading"
    className={cn('flex items-start justify-between gap-4', className)}
    {...props}
  >
    <div className={cn(inset && 'px-4')}>
      <h2 className="text-xl font-medium text-gray-800">{title}</h2>
      {description && <p className="mt-1 max-w-2xl text-sm text-gray-500">{description}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
));
SettingsSectionHeading.displayName = 'SettingsSectionHeading';

interface SettingsSectionProps extends Omit<React.ComponentProps<'section'>, 'title'> {
  title?: string;
  description?: React.ReactNode;
  /** Rendered to the right of the heading. */
  action?: React.ReactNode;
  /** Rendered as the last divided block inside the card. */
  footer?: React.ReactNode;
}

const SettingsSection = React.forwardRef<HTMLElement, SettingsSectionProps>(
  ({ title, description, action, footer, className, children, ...props }, ref) => (
    <section ref={ref} data-settings-section="root" className={cn('space-y-8', className)} {...props}>
      {title && <SettingsSectionHeading title={title} description={description} action={action} />}
      <div
        data-settings-section="card"
        className="divide-y divide-gray-200 overflow-hidden rounded-xl border bg-white shadow-lg/1"
      >
        {children}
        {footer}
      </div>
    </section>
  ),
);
SettingsSection.displayName = 'SettingsSection';

const SettingsRow = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<'div'>, 'title'> & { title: React.ReactNode; description?: React.ReactNode }
>(({ title, description, className, children, ...props }, ref) => (
  <div
    ref={ref}
    data-settings-section="row"
    className={cn('flex items-center justify-between gap-8 px-5 py-4 pr-4', className)}
    {...props}
  >
    <div className="min-w-0">
      <div className="text-sm font-medium text-gray-800">{title}</div>
      {description && <div className="mt-0.5 space-y-2 text-sm text-gray-500">{description}</div>}
    </div>

    {children && <div className="shrink-0">{children}</div>}
  </div>
));
SettingsRow.displayName = 'SettingsRow';

/**
 * The header row of a list card: a label on the left (e.g. a count or empty
 * message) and an optional add button on the right. Sits as the first divided
 * block inside a `SettingsSection`, above the `SettingsListItem` rows.
 */
const SettingsListHeader = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<'div'>, 'title'> & {
    label: React.ReactNode;
    onAdd?: () => void;
    addLabel?: string;
    addDisabled?: boolean;
  }
>(({ label, onAdd, addLabel = 'Add', addDisabled, className, ...props }, ref) => (
  <div
    ref={ref}
    data-settings-section="list-header"
    className={cn('flex items-center justify-between gap-4 px-5 py-3', className)}
    {...props}
  >
    <span className="text-sm font-medium text-gray-700">{label}</span>
    {onAdd && (
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        onClick={onAdd}
        disabled={addDisabled}
        aria-label={addLabel}
        className="text-gray-400 hover:text-gray-900"
      >
        <Icon className="lucide--plus size-4" />
      </Button>
    )}
  </div>
));
SettingsListHeader.displayName = 'SettingsListHeader';

/**
 * A single row in a list card: a `title` (and optional `description`) on the
 * left, optional `leading` content (e.g. an index), and an `actions` slot on
 * the right for an overflow menu. Highlights on hover; clickable when `onClick`
 * is provided.
 */
const SettingsListItem = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<'div'>, 'title'> & {
    title: React.ReactNode;
    description?: React.ReactNode;
    leading?: React.ReactNode;
    actions?: React.ReactNode;
  }
>(({ title, description, leading, actions, className, onClick, ...props }, ref) => (
  <div
    ref={ref}
    data-settings-section="list-item"
    onClick={onClick}
    className={cn(
      'group flex items-center justify-between gap-4 px-5 py-3 transition-colors',
      onClick && 'cursor-pointer hover:bg-gray-50',
      className,
    )}
    {...props}
  >
    <div className="flex min-w-0 items-start gap-3">
      {leading}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-gray-800">{title}</div>
        {description && <div className="mt-0.5 truncate text-sm text-gray-500">{description}</div>}
      </div>
    </div>
    {actions && <div className="shrink-0">{actions}</div>}
  </div>
));
SettingsListItem.displayName = 'SettingsListItem';

const SettingsSectionFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-settings-section="footer"
      className={cn('flex items-center justify-between gap-4 bg-gray-50 px-4 py-3', className)}
      {...props}
    />
  ),
);
SettingsSectionFooter.displayName = 'SettingsSectionFooter';

const saveStatusDotClasses: Record<'saved' | 'unsaved' | 'neutral', string> = {
  saved: 'bg-green-500',
  unsaved: 'bg-amber-500',
  neutral: 'bg-gray-400',
};

const SettingsSaveStatus = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<'span'> & { variant: 'saved' | 'unsaved' | 'neutral' }
>(({ variant, className, children, ...props }, ref) => (
  <span
    ref={ref}
    data-settings-section="save-status"
    className={cn('inline-flex items-center gap-3 pl-2 text-sm text-gray-500', className)}
    {...props}
  >
    <span className={cn('size-1.5 rounded-full', saveStatusDotClasses[variant])} />
    {children}
  </span>
));
SettingsSaveStatus.displayName = 'SettingsSaveStatus';

const SettingsSaveButton = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<typeof Button>, 'children'> & {
    isLoading?: boolean;
    hasUnsavedChanges?: boolean;
    label?: string;
    /** When the button is disabled, shows this text in a tooltip explaining why. */
    disabledReason?: string;
  }
>(
  (
    { isLoading = false, hasUnsavedChanges = true, label = 'Save Changes', disabledReason, disabled, ...props },
    ref,
  ) => {
    const isDisabled = isLoading || disabled;

    const button = (
      <Button ref={ref} variant={hasUnsavedChanges ? 'primary' : 'white'} disabled={isDisabled} {...props}>
        {isLoading ? 'Saving...' : label}
      </Button>
    );

    // A disabled button doesn't fire pointer events, so wrap it in a focusable span
    // that can trigger the tooltip on hover/focus.
    if (isDisabled && disabledReason) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">
              {button}
            </span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      );
    }

    return button;
  },
);
SettingsSaveButton.displayName = 'SettingsSaveButton';

export {
  SettingsContent,
  SettingsBackLink,
  SettingsSection,
  SettingsSectionHeading,
  SettingsRow,
  SettingsListHeader,
  SettingsListItem,
  SettingsSectionFooter,
  SettingsSaveStatus,
  SettingsSaveButton,
};
